import { Badge } from "@/app/admin/_common/ui/badge";
import type { LinkPreview } from "../../linksPageModel";

const SOURCE_LABELS: Record<LinkPreview["source"], string> = {
  custom: "Custom",
  show: "From linked show",
  default: "Site default",
};

const SOURCE_BADGE_CLASSES: Record<LinkPreview["source"], string> = {
  custom: "border-emerald-200 bg-emerald-50 text-emerald-700",
  show: "border-blue-200 bg-blue-50 text-blue-700",
  default: "border-slate-200 bg-slate-50 text-slate-600",
};

const SOURCE_DOT_CLASSES: Record<LinkPreview["source"], string> = {
  custom: "bg-emerald-500",
  show: "bg-blue-500",
  default: "bg-slate-400",
};

/**
 * Approximates how the link unfurls in iMessage/Slack. Values come from the
 * server-resolved preview, overridden live by whatever is currently typed so
 * the card tracks edits before they're saved.
 */
export default function SharePreviewCard({
  preview,
  title,
  description,
  image,
}: {
  preview: LinkPreview | null;
  title?: string;
  description?: string;
  image?: string | null;
}) {
  const shownTitle = title?.trim() || preview?.title || "";
  const shownDescription = description?.trim() || preview?.description || "";
  const shownImage = image || preview?.image || "";
  // Anything typed locally is a custom value, even before it's saved.
  const source: LinkPreview["source"] =
    title?.trim() || description?.trim() || image ? "custom" : preview?.source ?? "default";

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] text-muted-foreground">Preview</span>
        <Badge
          variant="outline"
          className={`gap-1.5 rounded-[7px] font-normal ${SOURCE_BADGE_CLASSES[source]}`}
        >
          <span className={`size-1.5 flex-none rounded-full ${SOURCE_DOT_CLASSES[source]}`} />
          {SOURCE_LABELS[source]}
        </Badge>
      </div>

      <div className="overflow-hidden rounded-md border border-border">
        {shownImage ? (
          <img
            src={shownImage}
            alt=""
            className="aspect-[1.91/1] w-full bg-muted object-cover"
          />
        ) : (
          <div className="aspect-[1.91/1] w-full bg-muted" />
        )}
        <div className="grid gap-1 p-3">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            comedyuo.com
          </span>
          <span className="line-clamp-2 text-[13px] font-medium text-foreground">
            {shownTitle}
          </span>
          <span className="line-clamp-2 text-[12.5px] text-muted-foreground">
            {shownDescription}
          </span>
        </div>
      </div>
    </div>
  );
}
